import type { CaptureResult, PostHog } from "posthog-js";

type ProductAnalyticsClient = Pick<
  PostHog,
  "capture" | "get_distinct_id" | "identify" | "register" | "reset"
>;

type StorageLike = Pick<Storage, "getItem" | "setItem">;

type DesktopRuntimeInfo = {
  version?: string;
  platform?: string;
  channel?: string;
  desktopAnalyticsVersion?: number;
};

type DesktopInvoke = <T = unknown>(
  command: string,
  args?: Record<string, unknown>,
) => Promise<T>;

type AnalyticsWindow = {
  __FINDIN_DESKTOP_INFO__?: unknown;
  __TAURI_INTERNALS__?: {
    invoke?: DesktopInvoke;
  };
  navigator?: Pick<Navigator, "platform" | "userAgent">;
  localStorage?: StorageLike;
};

type AuthMethod = "phone_sms" | "wechat_oauth" | "wechat_qr";

export type ProductEventProperties = {
  desktop_download_clicked: {
    platform: "mac" | "windows";
    source_id: string;
    option_id: string;
    file_type: string;
    placement: "hero" | "platform_options";
    target_version?: string;
  };
  desktop_first_launch: {
    launch_scope: "installation";
  };
  signup_completed: {
    auth_method: AuthMethod;
  };
  login_succeeded: {
    auth_method: AuthMethod;
  };
  paper_import_succeeded: {
    source: string;
    subject_context: "library" | "subject" | "import_list";
    file_type: string;
    duration_ms: number;
  };
  task_completed: {
    task_type: "paper_chat";
    used_selection: boolean;
  };
  first_task_completed: {
    task_type: "paper_chat";
    used_selection: boolean;
  };
  payment_succeeded: {
    payment_type: "upgrade" | "upgradePlus3x" | "recharge";
    amount: number;
    currency: "CNY";
    confirmation_source: "payment_websocket";
  };
};

export type ProductEventName = keyof ProductEventProperties;

const ONCE_STORAGE_PREFIX = "findin:posthog-once:v1:";
const DESKTOP_FIRST_LAUNCH_SCOPE = "desktop-installation";
const DESKTOP_ANALYTICS_VERSION = 1;
const HAS_DESKTOP_FIRST_LAUNCH_MARKER_COMMAND = "has_desktop_first_launch_marker";
const MARK_DESKTOP_FIRST_LAUNCH_RECORDED_COMMAND = "mark_desktop_first_launch_recorded";
const memoryOnceKeys = new Set<string>();
const desktopFirstLaunchWindows = new WeakSet<object>();
let activeClient: ProductAnalyticsClient | null = null;
let desktopFirstLaunchCapture: Promise<boolean> | null = null;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function asSafeString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function asSafeNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function getRuntimeWindow(): AnalyticsWindow | null {
  return typeof window === "undefined" ? null : window as AnalyticsWindow;
}

function getDesktopRuntimeInfo(runtimeWindow: AnalyticsWindow | null): DesktopRuntimeInfo | null {
  const rawInfo = asRecord(runtimeWindow?.__FINDIN_DESKTOP_INFO__);
  if (!rawInfo) {
    return null;
  }

  return {
    version: asSafeString(rawInfo.version),
    platform: asSafeString(rawInfo.platform),
    channel: asSafeString(rawInfo.channel),
    desktopAnalyticsVersion: asSafeNumber(rawInfo.desktopAnalyticsVersion),
  };
}

function getAnalyticsEnvironment() {
  return asSafeString(process.env.NEXT_PUBLIC_PACKAGE) ?? "unknown";
}

function normalizeBrowserPlatform(navigatorValue?: AnalyticsWindow["navigator"]): string {
  const platform = `${navigatorValue?.platform ?? ""} ${navigatorValue?.userAgent ?? ""}`.toLowerCase();
  if (platform.includes("windows") || platform.includes("win32")) return "windows";
  if (platform.includes("android")) return "android";
  if (platform.includes("iphone") || platform.includes("ipad") || platform.includes("ios")) return "ios";
  if (platform.includes("mac")) return "macos";
  if (platform.includes("linux")) return "linux";
  return "unknown";
}

export function getProductEventCommonProperties(
  runtimeWindow: AnalyticsWindow | null = getRuntimeWindow(),
) {
  const desktopInfo = getDesktopRuntimeInfo(runtimeWindow);
  if (!desktopInfo) {
    return {
      app_surface: "web" as const,
      app_environment: getAnalyticsEnvironment(),
      client_platform: normalizeBrowserPlatform(runtimeWindow?.navigator),
    };
  }

  return {
    app_surface: "desktop" as const,
    app_environment: getAnalyticsEnvironment(),
    client_platform: desktopInfo.platform ?? "unknown",
    ...(desktopInfo.version ? { client_version: desktopInfo.version } : {}),
    ...(desktopInfo.channel ? { release_channel: desktopInfo.channel } : {}),
  };
}

function getStorage(runtimeWindow: AnalyticsWindow | null = getRuntimeWindow()): StorageLike | null {
  try {
    return runtimeWindow?.localStorage ?? null;
  } catch {
    return null;
  }
}

function hashScope(value: string) {
  let hash = 5381;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) + hash) ^ value.charCodeAt(index);
  }
  return (hash >>> 0).toString(36);
}

function hasOnceMarker(storageKey: string, storage: StorageLike | null) {
  if (memoryOnceKeys.has(storageKey)) {
    return true;
  }

  try {
    return storage?.getItem(storageKey) === "1";
  } catch {
    return false;
  }
}

function writeOnceMarker(storageKey: string, storage: StorageLike | null) {
  memoryOnceKeys.add(storageKey);
  try {
    storage?.setItem(storageKey, "1");
  } catch {
    // localStorage can be unavailable in private or restricted browser modes.
  }
}

function hasDesktopFirstLaunchMarker(
  storageKey: string,
  storage: StorageLike | null,
  runtimeWindow: AnalyticsWindow | null,
) {
  if (runtimeWindow && desktopFirstLaunchWindows.has(runtimeWindow)) {
    return true;
  }

  try {
    return storage?.getItem(storageKey) === "1";
  } catch {
    return false;
  }
}

function hasDesktopFirstLaunchMemoryMarker(runtimeWindow: AnalyticsWindow | null) {
  return Boolean(runtimeWindow && desktopFirstLaunchWindows.has(runtimeWindow));
}

function writeDesktopFirstLaunchMarker(
  storageKey: string,
  storage: StorageLike | null,
  runtimeWindow: AnalyticsWindow | null,
) {
  if (runtimeWindow) {
    desktopFirstLaunchWindows.add(runtimeWindow);
  }
  try {
    storage?.setItem(storageKey, "1");
  } catch {
    // Native persistence remains authoritative when localStorage is unavailable.
  }
}

function getDesktopAnalyticsInvoke(
  runtimeWindow: AnalyticsWindow | null,
  desktopInfo: DesktopRuntimeInfo,
): DesktopInvoke | null {
  const invoke = runtimeWindow?.__TAURI_INTERNALS__?.invoke;
  return (desktopInfo.desktopAnalyticsVersion ?? 0) >= DESKTOP_ANALYTICS_VERSION
    && typeof invoke === "function"
    ? invoke
    : null;
}

async function hasNativeDesktopFirstLaunchMarker(invoke: DesktopInvoke): Promise<boolean | null> {
  try {
    return await invoke<boolean>(HAS_DESKTOP_FIRST_LAUNCH_MARKER_COMMAND) === true;
  } catch {
    return null;
  }
}

async function writeNativeDesktopFirstLaunchMarker(invoke: DesktopInvoke | null) {
  if (!invoke) {
    return false;
  }

  try {
    await invoke(MARK_DESKTOP_FIRST_LAUNCH_RECORDED_COMMAND);
    return true;
  } catch {
    // Keep the localStorage marker as a fallback if native persistence fails.
    return false;
  }
}

export function configureProductAnalytics(client: ProductAnalyticsClient) {
  activeClient = client;
  client.register(getProductEventCommonProperties());
}

export function identifyProductAnalyticsUser(
  userId: string | number | null | undefined,
  client: ProductAnalyticsClient | null = activeClient,
) {
  const normalizedUserId = userId == null ? "" : String(userId).trim();
  if (!client || !normalizedUserId) {
    return false;
  }

  client.identify(normalizedUserId);
  return true;
}

export function resetProductAnalyticsIdentity(
  client: ProductAnalyticsClient | null = activeClient,
) {
  if (!client) {
    return false;
  }

  client.reset();
  client.register(getProductEventCommonProperties());
  return true;
}

export function captureProductEvent<EventName extends ProductEventName>(
  eventName: EventName,
  properties: ProductEventProperties[EventName],
  client: ProductAnalyticsClient | null = activeClient,
): CaptureResult | undefined {
  if (!client) {
    return undefined;
  }

  return client.capture(eventName, {
    ...getProductEventCommonProperties(),
    ...properties,
  });
}

function captureProductEventOnce<EventName extends ProductEventName>(
  eventName: EventName,
  properties: ProductEventProperties[EventName],
  scope: string,
  storage: StorageLike | null,
  client: ProductAnalyticsClient | null,
) {
  if (!client) {
    return false;
  }

  const storageKey = `${ONCE_STORAGE_PREFIX}${eventName}:${hashScope(scope)}`;
  if (hasOnceMarker(storageKey, storage)) {
    return false;
  }

  const capturedEvent = captureProductEvent(eventName, properties, client);
  if (!capturedEvent) {
    return false;
  }

  writeOnceMarker(storageKey, storage);
  return true;
}

async function captureDesktopFirstLaunchOnce({
  client = activeClient,
  runtimeWindow = getRuntimeWindow(),
  storage = getStorage(runtimeWindow),
}: {
  client?: ProductAnalyticsClient | null;
  runtimeWindow?: AnalyticsWindow | null;
  storage?: StorageLike | null;
} = {}) {
  const desktopInfo = getDesktopRuntimeInfo(runtimeWindow);
  if (!desktopInfo) {
    return false;
  }

  const storageKey = `${ONCE_STORAGE_PREFIX}desktop_first_launch:${hashScope(DESKTOP_FIRST_LAUNCH_SCOPE)}`;
  const nativeInvoke = getDesktopAnalyticsInvoke(runtimeWindow, desktopInfo);

  // Migrate the marker written by v1.9.1 Web code before native persistence
  // existed. This avoids reporting an existing installation as new.
  if (hasDesktopFirstLaunchMemoryMarker(runtimeWindow)) {
    return false;
  }
  if (hasDesktopFirstLaunchMarker(storageKey, storage, runtimeWindow)) {
    await writeNativeDesktopFirstLaunchMarker(nativeInvoke);
    return false;
  }

  if (nativeInvoke) {
    const hasNativeMarker = await hasNativeDesktopFirstLaunchMarker(nativeInvoke);
    if (hasNativeMarker) {
      writeDesktopFirstLaunchMarker(storageKey, storage, runtimeWindow);
      return false;
    }

    // A second caller may have completed while the native read was pending.
    if (hasDesktopFirstLaunchMemoryMarker(runtimeWindow)) {
      return false;
    }
    if (hasDesktopFirstLaunchMarker(storageKey, storage, runtimeWindow)) {
      await writeNativeDesktopFirstLaunchMarker(nativeInvoke);
      return false;
    }
  }

  if (!client) {
    return false;
  }

  const capturedEvent = captureProductEvent(
    "desktop_first_launch",
    { launch_scope: "installation" },
    client,
  );
  if (!capturedEvent) {
    return false;
  }

  writeDesktopFirstLaunchMarker(storageKey, storage, runtimeWindow);
  await writeNativeDesktopFirstLaunchMarker(nativeInvoke);
  return true;
}

export async function captureDesktopFirstLaunch(options: {
  client?: ProductAnalyticsClient | null;
  runtimeWindow?: AnalyticsWindow | null;
  storage?: StorageLike | null;
} = {}) {
  if (desktopFirstLaunchCapture) {
    return desktopFirstLaunchCapture;
  }

  const capture = captureDesktopFirstLaunchOnce(options);
  desktopFirstLaunchCapture = capture;
  try {
    return await capture;
  } finally {
    if (desktopFirstLaunchCapture === capture) {
      desktopFirstLaunchCapture = null;
    }
  }
}

export function captureTaskCompleted(
  properties: ProductEventProperties["task_completed"],
  userScope?: string | number | null,
  {
    client = activeClient,
    storage = getStorage(),
  }: {
    client?: ProductAnalyticsClient | null;
    storage?: StorageLike | null;
  } = {},
) {
  if (!client) {
    return false;
  }

  const capturedTask = captureProductEvent("task_completed", properties, client);
  if (!capturedTask) {
    return false;
  }

  let distinctId = "anonymous";
  try {
    distinctId = client.get_distinct_id() || distinctId;
  } catch {
    // Keep a deterministic fallback when the client has not exposed an ID.
  }

  captureProductEventOnce(
    "first_task_completed",
    properties,
    `user:${userScope == null ? distinctId : String(userScope)}`,
    storage,
    client,
  );
  return true;
}

function getPayloadRows(payload: unknown) {
  const root = asRecord(payload);
  if (!root) {
    return [];
  }
  const nestedData = asRecord(root.data);
  return nestedData ? [root, nestedData] : [root];
}

export function extractAnalyticsUserId(payload: unknown): string | null {
  for (const row of getPayloadRows(payload)) {
    const candidate = row.userId ?? row.user_id;
    if ((typeof candidate === "string" || typeof candidate === "number") && String(candidate).trim()) {
      return String(candidate).trim();
    }
  }
  return null;
}

export function hasExplicitNewUserFlag(payload: unknown) {
  const flagNames = ["isNewUser", "is_new_user", "newUser", "new_user"];
  return getPayloadRows(payload).some((row) => flagNames.some((flagName) => {
    const value = row[flagName];
    return value === true || value === 1 || value === "1";
  }));
}
